import React, { useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../../styles/theme';
import { DAY_PROFILE, FAN_REASONS, MEALS, dayFullKey, rowOf, dayOf } from '../constants';
import { useTranslation } from 'react-i18next';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import type { MealEntryType } from '../../../../redux/mealEntry';
import type { MealSlotState } from '../dayAddTarget';
import { TimeChip } from '../primitives/TimeChip';
import { CardMeta } from '../primitives/CardMeta';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';
import { SideFoodChips } from '../SideFoodChips';
import type { SideFoodsProps } from '../sideFoodsState';
import { FanChipBar } from '../FanChipBar';
import type { FanChipBarProps } from '../useFanChoice';
import { resolveRecipeTitle } from '../../../../utils/recipeTitle';

export interface FanOption {
  key: string;
  recipe: RecipeIndexEntry;
  consequence?: string;
  /** FAN_REASONS key ('fast' | 'fridge' | …) — falls back to position when absent. */
  angleKey?: string;
  /** Label and colour for an option that is not one of FAN_REASONS (an accompaniment). */
  reason?: { labelKey?: string; accent: string };
}

interface Props {
  sk: number;
  currentRecipe?: RecipeIndexEntry;
  options: FanOption[];
  profile: typeof DAY_PROFILE[0];
  /**
   * The drawer is choosing an accompaniment for `currentRecipe` (#746), which
   * stays on the menu: no day profile, no restaurant or leftovers, no remove.
   */
  sides?: boolean;
  /**
   * The foods the cook can ask the accompaniment list to use (#853), with the
   * ones picked. Only read while choosing an accompaniment.
   */
  sideFoods?: SideFoodsProps;
  /** « 5 nouvelles » and the meal's chips (#864). */
  chipBar?: FanChipBarProps;
  onPickRecipe: (recipeId: string) => void;
  onConvert: (kind: ConvertKind) => void;
  /**
   * Breakfast, lunch and dinner of the slot's day. When given (adding to an
   * empty slot), the drawer offers a switch so a cook who opened the wrong
   * row moves to the right meal before anything is written.
   */
  mealStates?: MealSlotState[];
  onSwitchMeal?: (mi: number) => void;
  onOpenDetail: () => void;
  onOpenLibrary: () => void;
  onRemove: () => void;
  onClose: () => void;
}

const isWeb = Platform.OS === 'web';

type ConvertKind = 'dining' | 'leftover' | 'skip' | 'note';

/** The simple meal comes first: it is the one a cook reaches for most. */
const CONVERT_TILES: { kind: ConvertKind; type: MealEntryType; labelKey: string; hintKey: string }[] = [
  { kind: 'note',     type: 'note',     labelKey: 'entry.note',        hintKey: 'entry.subNoteHint' },
  { kind: 'leftover', type: 'leftover', labelKey: 'entry.leftovers',   hintKey: 'entry.leftoversHint' },
  { kind: 'dining',   type: 'dining',   labelKey: 'entry.dining',      hintKey: 'entry.diningLong' },
  { kind: 'skip',     type: 'skip',     labelKey: 'entry.nothingShort', hintKey: 'entry.skipHint' },
];

export function ReplaceDrawer({
  sk,
  currentRecipe,
  options,
  profile,
  sides = false,
  sideFoods,
  chipBar,
  onPickRecipe,
  onConvert,
  mealStates,
  onSwitchMeal,
  onOpenDetail,
  onOpenLibrary,
  onRemove,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const translateX = useRef(new Animated.Value(432)).current;
  const di = sk % 7;
  const mi = Math.floor(sk / 7);
  const mealLabel = MEALS[mi] ? t(MEALS[mi].labelKey) : '';
  const dayLabel = t(dayFullKey(di));

  useEffect(() => {
    Animated.timing(translateX, {
      toValue: 0,
      duration: 340,
      useNativeDriver: true,
    }).start();
  }, [translateX]);

  const [hoveredOptionId, setHoveredOptionId] = useState<string | null>(null);

  return (
    <>
      {/* Scrim Pressable */}
      <Pressable style={styles.scrim} onPress={onClose} />

      {/* Drawer */}
      <Animated.View
        style={[
          styles.drawer,
          isWeb && { transform: [{ translateX }] } as any,
        ]}
      >
        {/* Fixed header */}
        <View
          style={[
            styles.header,
            isWeb && {
              backgroundColor: 'rgba(251, 249, 246, 0.85)',
              backdropFilter: 'blur(20px)',
            } as any,
          ]}
        >
          <View style={styles.headerTop}>
            <Text style={styles.headerTitle} testID={sides ? 'sides-drawer' : undefined}>
              {sides ? t('thisWeek.goesWith') : currentRecipe ? t('entry.replaceDish') : t('entry.addMeal')}
            </Text>
            <Pressable style={styles.headerClose} onPress={onClose} testID="replace-drawer-close">
              <Feather name="x" size={20} color={Paprika.textSecondary} />
            </Pressable>
          </View>
          {mealStates && onSwitchMeal && !currentRecipe && !sides ? (
            <>
              <Text style={styles.headerSubtitle}>{dayLabel}</Text>
              <View style={styles.mealSwitch} accessibilityRole="tablist">
                {MEALS.map((meal, i) => {
                  const on = i === mi;
                  const state = mealStates[i];
                  const disabled = !on && state !== 'free';
                  return (
                    <Pressable
                      key={meal.id}
                      style={[styles.mealOption, on && styles.mealOptionOn, disabled && styles.mealOptionOff]}
                      onPress={() => { if (!on && !disabled) onSwitchMeal(i); }}
                      disabled={disabled}
                      accessibilityRole="tab"
                      accessibilityState={{ selected: on, disabled }}
                      testID={`meal-switch-${meal.mealType}`}
                    >
                      <Text style={[styles.mealOptionText, on && styles.mealOptionTextOn]}>{t(meal.labelKey)}</Text>
                      <Text style={[styles.mealOptionState, on && styles.mealOptionTextOn]}>
                        {t(`thisWeek.mealSlotState.${on ? 'free' : state}`)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </>
          ) : (
            <Text style={styles.headerSubtitle}>{dayLabel} · {mealLabel}</Text>
          )}

          {/* Current meal row */}
          {currentRecipe && (
            <View style={styles.currentRow}>
              {/* Photo */}
              <View style={styles.currentPhoto}>
                <RemoteImage
                  uri={currentRecipe.image}
                  variant="thumb"
                  style={styles.currentPhotoImage}
                  resizeMode="cover"
                  fallback={<View style={[styles.currentPhotoImage, styles.photoPlaceholder]} />}
                />
              </View>

              {/* Info */}
              <View style={styles.currentInfo}>
                <Text style={styles.currentKicker}>{t('entry.currently')}</Text>
                <Text style={styles.currentTitle} numberOfLines={1}>{currentRecipe.title}</Text>
              </View>

              {/* Voir button */}
              <Pressable style={styles.voirBtn} onPress={onOpenDetail}>
                <Text style={styles.voirText}>{t('entry.view')}</Text>
              </Pressable>
            </View>
          )}

          {/* Day profile chip — advice for choosing a dinner, so a dinner only:
              « Réconfort — jeudi soir » above a breakfast was nonsense. */}
          {!sides && mi === 2 && <View
            style={[
              styles.profileChip,
              { backgroundColor: profile.accent + '14' },
            ]}
          >
            <View style={[styles.profileDot, { backgroundColor: profile.accent }]} />
            <Text style={[styles.profileText, { color: profile.accent }]}>
              <Text style={styles.profileTag}>{t(profile.tagKey)}</Text>
              {' — '}
              <Text>{t(profile.noteKey)}</Text>
            </Text>
          </View>}
        </View>

        {/* Scrollable body */}
        <ScrollView
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator={false}
        >
          {sides && !!sideFoods && <SideFoodChips {...sideFoods} inset={0} />}

          {/* Without a recipe — four equal choices. They used to be small pills
              under « Pas de recette ce jour-là ? », which reads as skipping the
              day, and a tuna sandwich had nowhere to go but « Note » (8 Oct 2026). */}
          {!sides && <View style={styles.conversionBar}>
            <Text style={styles.fanKicker}>{t('thisWeek.noRecipeThatDay')}</Text>
            <View style={styles.tiles}>
              {CONVERT_TILES.map((tile) => {
                const m = ENTRY_META[tile.type];
                return (
                  <Pressable
                    key={tile.kind}
                    style={({ pressed }) => [styles.tile, pressed && styles.tilePressed]}
                    onPress={() => onConvert(tile.kind)}
                    testID={`convert-${tile.kind}`}
                    accessibilityRole="button"
                  >
                    <View style={[styles.tileIcon, { backgroundColor: m.tint }]}>
                      <EntryIcon type={tile.type} size={17} />
                    </View>
                    <View style={styles.tileText}>
                      <Text style={styles.tileTitle} numberOfLines={1}>{t(tile.labelKey)}</Text>
                      <Text style={styles.tileHint} numberOfLines={2}>{t(tile.hintKey)}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </View>}

          {/* Fan options kicker */}
          {!sides && <Text style={styles.fanKicker}>
            {options.length === 0
              ? t('entry.ideasForYou')
              : t('entry.ideaForYou', { count: options.length })}
          </Text>}

          {!sides && !!chipBar && <FanChipBar {...chipBar} inset={0} wrap />}

          {/* Empty state — library too small to suggest alternatives */}
          {options.length === 0 && (
            <Text style={styles.fanEmpty}>
              {sides ? t('thisWeek.sidesEmpty') : t('entry.noOtherRecipes')}
            </Text>
          )}

          {/* Fan rows */}
          <View style={styles.fanRows}>
            {options.map((opt, i) => {
              const reason = opt.reason ??
                FAN_REASONS.find((r) => r.key === opt.angleKey) ?? FAN_REASONS[i % FAN_REASONS.length];
              const recipe = opt.recipe;
              const isHovered = hoveredOptionId === opt.key;

              return (
                <Pressable
                  key={opt.key}
                  style={styles.fanRow}
                  testID={`fan-option-${recipe.id}`}
                  onPress={() => onPickRecipe(recipe.id)}
                  {...(isWeb && {
                    onHoverIn: () => setHoveredOptionId(opt.key),
                    onHoverOut: () => setHoveredOptionId(null),
                  } as any)}
                >
                  {/* Photo */}
                  <View style={styles.fanPhoto}>
                    <RemoteImage
                      uri={recipe.image}
                      variant="thumb"
                      style={styles.fanPhotoImage}
                      resizeMode="cover"
                      fallback={<View style={[styles.fanPhotoImage, styles.photoPlaceholder]} />}
                    />
                  </View>

                  {/* Content */}
                  <View style={styles.fanContent}>
                    {/* Angle pill */}
                    {!!reason.labelKey && (
                      <View
                        style={[
                          styles.anglePill,
                          { backgroundColor: reason.accent + '18' },
                        ]}
                      >
                        <Text style={[styles.anglePillText, { color: reason.accent }]}>
                          {t(reason.labelKey)}
                        </Text>
                      </View>
                    )}

                    {/* Title */}
                    <Text style={styles.fanTitle} numberOfLines={2}>{resolveRecipeTitle(recipe, i18n.language)}</Text>

                    {/* Time and rating row */}
                    <View style={styles.metaRow}>
                      {recipe.readyInMinutes != null && (
                        <TimeChip minutes={recipe.readyInMinutes} />
                      )}
                      <CardMeta
                        sourceUrl={recipe.sourceUrl}
                        rating={recipe.sourceRating}
                        minutes={undefined}
                      />
                    </View>

                    {/* Consequence line */}
                    {!!opt.consequence && (
                      <Text
                        style={[styles.consequence, { color: reason.accent }]}
                        numberOfLines={1}
                      >
                        {opt.consequence}
                      </Text>
                    )}
                  </View>

                  {/* Chevron / Check */}
                  <View style={styles.fanCta}>
                    <Text style={styles.fanCtaText}>
                      {isHovered ? '✓' : '›'}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </ScrollView>

        {/* Fixed footer */}
        <View style={styles.footer}>
          <Pressable style={styles.footerBtn} onPress={onOpenLibrary} testID="replace-open-library">
            <Feather name="search" size={16} color={Paprika.textSecondary} />
            <Text style={styles.footerBtnText}>{sides ? t('thisWeek.sidesOther') : t('thisWeek.wholeLibrary')}</Text>
          </Pressable>
          {currentRecipe && !sides && (
            <Pressable style={styles.footerBtn} onPress={onRemove}>
              <Text style={styles.footerBtnText}>{t('thisWeek.remove')}</Text>
            </Pressable>
          )}
        </View>
      </Animated.View>
    </>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    zIndex: 59,
  },
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 432,
    backgroundColor: Paprika.background,
    zIndex: 60,
    flexDirection: 'column',
    ...(isWeb && {
      boxShadow: '-18px 0 50px -20px rgba(82,68,60,0.3)',
    } as any),
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 12,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  headerClose: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  currentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 8,
    paddingVertical: 10,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 8,
  },
  currentPhoto: {
    width: 46,
    height: 46,
    borderRadius: 6,
    overflow: 'hidden',
    flexShrink: 0,
  },
  currentPhotoImage: {
    width: '100%',
    height: '100%',
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  currentInfo: {
    flex: 1,
    gap: 2,
  },
  currentKicker: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  currentTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 16,
    color: Paprika.textPrimary,
  },
  voirBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    flexShrink: 0,
  },
  voirText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.primary,
  },
  profileChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  profileDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  profileText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textPrimary,
  },
  profileTag: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: 12,
    paddingVertical: 16,
    gap: 16,
  },
  conversionBar: {
    gap: 8,
  },
  mealSwitch: {
    flexDirection: 'row',
    gap: 3,
    padding: 3,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceHighest,
  },
  mealOption: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 6,
    borderRadius: 9,
    ...(isWeb && { cursor: 'pointer' } as any),
  },
  mealOptionOn: {
    backgroundColor: Paprika.surfaceLowest,
    ...(isWeb && { boxShadow: '0 2px 8px rgba(82,68,60,0.08)' } as any),
  },
  mealOptionOff: {
    opacity: 0.45,
    ...(isWeb && { cursor: 'default' } as any),
  },
  mealOptionText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.textSecondary,
  },
  mealOptionState: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: Paprika.textMuted,
    marginTop: 1,
  },
  mealOptionTextOn: {
    color: Paprika.primary,
  },
  tiles: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tile: {
    flexBasis: '47%',
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    ...(isWeb && { boxShadow: '0 2px 8px rgba(82,68,60,0.08)', cursor: 'pointer' } as any),
  },
  tilePressed: {
    opacity: 0.85,
  },
  tileIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileText: {
    flex: 1,
    minWidth: 0,
  },
  tileTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textPrimary,
  },
  tileHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    lineHeight: 14,
    color: Paprika.textMuted,
    marginTop: 1,
  },
  fanKicker: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  fanEmpty: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 19,
    color: Paprika.textMuted,
    paddingVertical: 8,
  },
  fanRows: {
    gap: 12,
  },
  fanRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 8,
  },
  fanPhoto: {
    width: 104,
    height: 80,
    borderRadius: 6,
    overflow: 'hidden',
    flexShrink: 0,
  },
  fanPhotoImage: {
    width: '100%',
    height: '100%',
  },
  fanContent: {
    flex: 1,
    gap: 4,
  },
  anglePill: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  anglePillText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
  },
  fanTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 14.5,
    lineHeight: 18,
    color: Paprika.textPrimary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  consequence: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
  },
  fanCta: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  fanCtaText: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textSecondary,
  },
  footer: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: Paprika.surfaceLowest,
  },
  footerBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
  },
  footerBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
});
